import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import {
  DEFAULT_PANE_WIDTHS,
  MAX_PANE_WIDTHS,
  MIN_PANE_WIDTHS,
  clampPaneWidth,
  readPaneWidths,
  writePaneWidths,
  type PaneName,
  type PaneWidths,
} from './paneWidths';

/** 矢印キー 1 回で動かす幅（px）。 */
export const PANE_STEP_PX = 16;

export interface PaneWidthsState {
  widths: PaneWidths;
  /** 最小と最大に収めて覚える。 */
  setWidth(pane: PaneName, px: number): void;
}

const PaneWidthsContext = createContext<PaneWidthsState | null>(null);

/** Provider の外（部品だけを描くとき）は既定の幅で、動かしても何も起きない。 */
const OUTSIDE: PaneWidthsState = { widths: DEFAULT_PANE_WIDTHS, setWidth: () => undefined };

/** ペインの幅を持ち、変わるたびに localStorage に書く（U4）。 */
export function PaneWidthsProvider({ children }: { children: ReactNode }) {
  const [widths, setWidths] = useState<PaneWidths>(readPaneWidths);
  const loaded = useRef(false);
  useEffect(() => {
    // 読んだ直後に同じ値を書き戻さない。
    if (!loaded.current) {
      loaded.current = true;
      return;
    }
    writePaneWidths(widths);
  }, [widths]);
  const setWidth = useCallback(
    (pane: PaneName, px: number) => setWidths((current) => ({ ...current, [pane]: clampPaneWidth(pane, px) })),
    [],
  );
  return <PaneWidthsContext value={{ widths, setWidth }}>{children}</PaneWidthsContext>;
}

export function usePaneWidths(): PaneWidthsState {
  return useContext(PaneWidthsContext) ?? OUTSIDE;
}

export interface PaneResizerProps {
  pane: PaneName;
  /** 読み上げ名。 */
  label: string;
  /** 掴むのが列の右端（'end'。右へ動かすと広がる）か左端（'start'。右へ動かすと狭まる）か。 */
  edge: 'start' | 'end';
}

/**
 * ペインの境目のつまみ（U4）。マウスで掴んで動かすか、フォーカスして矢印キー（16px ずつ）で動かす。
 * 判断 U4 に無い操作（Home / End・Shift の倍率・ダブルクリックでの初期化）は持たせない。
 */
export function PaneResizer({ pane, label, edge }: PaneResizerProps) {
  const { widths, setWidth } = usePaneWidths();
  const width = widths[pane];
  const [dragging, setDragging] = useState(false);
  const start = useRef({ x: 0, width });
  // 右端のつまみは右へ動かすと広がり、左端のつまみは右へ動かすと狭まる。
  const sign = edge === 'end' ? 1 : -1;

  useEffect(() => {
    if (!dragging) return;
    const stop = () => setDragging(false);
    const move = (event: MouseEvent) => {
      // ブラウザの窓の外でボタンを離すと mouseup がページに届かないので、押していない移動が
      // 来た時点で掴みを解く（解かないと以後のマウス移動で幅が動き続ける）。
      if (event.buttons === 0) {
        stop();
        return;
      }
      setWidth(pane, start.current.width + sign * (event.clientX - start.current.x));
    };
    window.addEventListener('mousemove', move);
    window.addEventListener('mouseup', stop);
    return () => {
      window.removeEventListener('mousemove', move);
      window.removeEventListener('mouseup', stop);
    };
  }, [dragging, pane, setWidth, sign]);

  const onKeyDown = (event: { key: string; preventDefault(): void }) => {
    const step = PANE_STEP_PX * sign;
    if (event.key === 'ArrowRight') setWidth(pane, width + step);
    else if (event.key === 'ArrowLeft') setWidth(pane, width - step);
    else return;
    event.preventDefault();
  };

  return (
    <div
      className={dragging ? 'studio-resizer studio-resizer-dragging' : 'studio-resizer'}
      role="separator"
      aria-label={label}
      aria-orientation="vertical"
      aria-valuenow={width}
      aria-valuemin={MIN_PANE_WIDTHS[pane]}
      aria-valuemax={MAX_PANE_WIDTHS[pane]}
      tabIndex={0}
      onMouseDown={(event) => {
        // 掴んでいる間に文字が選ばれないようにする。
        event.preventDefault();
        start.current = { x: event.clientX, width };
        setDragging(true);
      }}
      onKeyDown={onKeyDown}
    />
  );
}
